import { useCallback } from 'react';

import { BRAND_VOICE_ASSET_FIELD } from 'src/data/brandVoiceAssets';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { GENERATION_MODE_TYPE, isSameLanguage, LANGUAGE } from 'src/data/generation';
import { ROUTES } from 'src/routes';
import { useErrorHandler, useHistoryPushOrInfinityOpen } from 'src/services';
import { useBrandVoicesActions, useCustomerCanCreateFeatureItem } from 'src/store';
import { useCustomFormulaPopup } from 'src/views/CustomFormulaPopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

export const useHandlers = ({ generationTool, onSelect, onMenuClose, subGenerationTool }) => {
  const brandVoicesActions = useBrandVoicesActions();
  const handleError = useErrorHandler();
  const openCustomFormulaPopup = useCustomFormulaPopup();
  const openUpgradePopup = useUpgradePopup();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();

  const customerCanCreateCustomFormula = useCustomerCanCreateFeatureItem(
    FEATURE_LIMITS.customFormulasPerCustomer
  );

  const handleAddFormula = useCallback(
    async (onClose) => {
      if (!customerCanCreateCustomFormula) {
        openUpgradePopup(FEATURE_LIMITS.customFormulasPerCustomer);
        return;
      }
      const brandVoicesAssets = brandVoicesActions
        .getBrandVoicesAssets()
        ?.filter((bva) => isSameLanguage(bva.language, LANGUAGE.en_us));

      if (brandVoicesAssets.length === 1) {
        try {
          const customFormula = await openCustomFormulaPopup({
            brandVoice: brandVoicesAssets[0],
            generationTool,
            filteredTools: [generationTool],
          });
          if (!customFormula) {
            return;
          }
          onSelect({ realId: customFormula.id, type: GENERATION_MODE_TYPE.customFormula });
          brandVoicesActions.storeBrandVoiceItemInCache({
            itemsField: BRAND_VOICE_ASSET_FIELD.customFormulas,
            item: customFormula,
            brandVoiceId: customFormula.brandVoice.id,
          });
        } catch (error) {
          handleError(error);
        } finally {
          onClose?.();
        }
      } else {
        historyPushOrInfinityOpen(
          `${ROUTES.BRAND_VOICE}/${brandVoicesAssets[0].id}${ROUTES.CUSTOM_FORMULA}`
        );
        onClose?.();
        if (onMenuClose) {
          onMenuClose();
        }
      }
    },
    [
      customerCanCreateCustomFormula,
      openUpgradePopup,
      openCustomFormulaPopup,
      generationTool,
      brandVoicesActions,
      onSelect,
      handleError,
      historyPushOrInfinityOpen,
      onMenuClose,
    ]
  );

  const handleSubGenerationToolAddFormula = useCallback(
    async (onClose) => {
      if (!customerCanCreateCustomFormula) {
        openUpgradePopup(FEATURE_LIMITS.customFormulasPerCustomer);
        return;
      }
      const brandVoicesAssets = brandVoicesActions.getBrandVoicesAssets();
      if (brandVoicesAssets.length === 1) {
        try {
          const customFormula = await openCustomFormulaPopup({
            brandVoice: brandVoicesAssets[0],
            subGenerationTool,
            filteredTools: [generationTool],
          });
          if (!customFormula) {
            return;
          }
          onSelect(
            {
              realId: customFormula.id,
              type: GENERATION_MODE_TYPE.customFormula,
            },
            subGenerationTool
          );
          brandVoicesActions.storeBrandVoiceItemInCache({
            itemsField: BRAND_VOICE_ASSET_FIELD.customFormulas,
            item: customFormula,
            brandVoiceId: customFormula.brandVoice.id,
          });
        } catch (error) {
          handleError(error);
        } finally {
          onClose?.();
        }
      } else {
        historyPushOrInfinityOpen(
          `${ROUTES.BRAND_VOICE}/${brandVoicesAssets[0].id}${ROUTES.CUSTOM_FORMULA}`
        );
        onClose?.();
        if (onMenuClose) {
          onMenuClose();
        }
      }
    },
    [
      customerCanCreateCustomFormula,
      brandVoicesActions,
      openUpgradePopup,
      openCustomFormulaPopup,
      subGenerationTool,
      generationTool,
      onSelect,
      handleError,
      historyPushOrInfinityOpen,
      onMenuClose,
    ]
  );

  return {
    handleAddFormula,
    handleSubGenerationToolAddFormula,
  };
};
